import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Sparkles, Shield, CheckCircle, Lock } from 'lucide-react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { supabase } from '@/integrations/supabase/client';
import { UserAwarePricingData } from '@/hooks/useUserAwarePricing';
import { trackGTMEvent, trackGTMConversionFunnel } from './GoogleTagManager';
import { trackEventMp, trackConversionFunnelMp } from './Mixpanel';
import { useToast } from '@/hooks/use-toast';
import { stripePromise } from '@/lib/stripe';
import { useTranslation } from 'react-i18next';

interface InlineCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail: string;
  selectedPlan: 'monthly' | 'yearly';
  pricingData: UserAwarePricingData;
}

export const InlineCheckoutModal = ({ 
  open, 
  onOpenChange, 
  userEmail, 
  selectedPlan,
  pricingData 
}: InlineCheckoutModalProps) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleClose = () => {
    setShowExitConfirm(true);
    trackGTMEvent('inline_checkout_close_attempted', { email: userEmail, plan: selectedPlan });
    trackEventMp('inline_checkout_close_attempted', { email: userEmail, plan: selectedPlan });
  };

  const handleConfirmExit = async () => {
    // Track abandonment
    try {
      await supabase.functions.invoke('track-checkout-abandonment', {
        body: {
          email: userEmail,
          reason: 'user_clicked_x',
          step: 'payment_modal'
        }
      });
    } catch (error) {
      console.error('[INLINE-CHECKOUT] Error tracking abandonment:', error);
    }

    trackGTMEvent('inline_checkout_abandoned', { email: userEmail, reason: 'confirmed_exit', plan: selectedPlan });
    trackEventMp('inline_checkout_abandoned', { email: userEmail, reason: 'confirmed_exit', plan: selectedPlan });
    setShowExitConfirm(false);
    onOpenChange(false);
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
    trackGTMEvent('inline_checkout_exit_cancelled', { email: userEmail, plan: selectedPlan });
    trackEventMp('inline_checkout_exit_cancelled', { email: userEmail, plan: selectedPlan });
  };

  const fetchClientSecret = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get prospect data from localStorage
      const prospectJSON = localStorage.getItem('newsletter_prospect');
      if (!prospectJSON) {
        throw new Error('No prospect data found');
      }
      
      const prospectData = JSON.parse(prospectJSON);
      
      // Validate data
      if (!prospectData.email || !prospectData.prospect_id) {
        throw new Error('Invalid prospect data');
      }
      
      // Check if data is too old (>1 hour)
      const dataAge = Date.now() - new Date(prospectData.timestamp).getTime();
      if (dataAge > 60 * 60 * 1000) {
        throw new Error('Session expired');
      }
      
      console.log('[INLINE-CHECKOUT] Creating checkout for prospect:', prospectData.email);

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          email: prospectData.email,
          plan: selectedPlan,
          checkout_flow: 'inline_v1'
        }
      });

      if (error) {
        console.error('[INLINE-CHECKOUT] Error creating checkout:', error);
        throw error;
      }

      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        console.log('[INLINE-CHECKOUT] Client secret received');
      } else {
        throw new Error('No client secret received');
      }
      
    } catch (error: any) {
      console.error('[INLINE-CHECKOUT] Error creating checkout:', {
        message: error.message,
        code: error.code,
        type: error.type,
        decline_code: error.decline_code,
        param: error.param,
        stack: error.stack
      });
      
      // Send detailed error to Slack
      try {
        const prospectJSON = localStorage.getItem('newsletter_prospect');
        const prospectData = prospectJSON ? JSON.parse(prospectJSON) : { email: userEmail };
        
        await supabase.functions.invoke('slack-notifications', {
          body: {
            table_name: 'create_checkout_frontend_error',
            record_data: {
              email: prospectData.email || userEmail,
              plan: selectedPlan,
              error_message: error.message,
              error_code: error.code,
              error_type: error.type,
              decline_code: error.decline_code,
              timestamp: new Date().toISOString()
            }
          }
        });
      } catch (slackError) {
        console.error('[INLINE-CHECKOUT] Failed to send error to Slack:', slackError);
      }
      
      // Show appropriate error message
      const errorMessage = error.message === 'Session expired' 
        ? t('inlineCheckout.errors.sessionExpired')
        : t('inlineCheckout.errors.generic');
        
      toast({
        title: t('inlineCheckout.errors.paymentProcessing'),
        description: errorMessage,
        variant: 'destructive'
      });
      
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [selectedPlan, onOpenChange, toast]);

  useEffect(() => {
    if (open) {
      fetchClientSecret();
      trackGTMConversionFunnel('inline_checkout_opened', 4);
      trackConversionFunnelMp('inline_checkout_opened', 4);
      trackGTMEvent('inline_checkout_modal_opened', { email: userEmail, plan: selectedPlan });
      trackEventMp('inline_checkout_modal_opened', { email: userEmail, plan: selectedPlan });
    }
  }, [open, selectedPlan]);

  const planPrice = pricingData.pricing?.[selectedPlan];
  const planName = selectedPlan === 'monthly' ? 'Mensal' : 'Anual';

  return (
    <>
      <Dialog open={open && !showExitConfirm} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-newsletter-primary/10 to-newsletter-secondary/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-newsletter-primary/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-newsletter-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-newsletter-primary">
                  🎉 Falta muito pouco!
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Você está a um passo de receber seu feed premium personalizado
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="p-6 space-y-6">
            {/* Trust Signals */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="space-y-2">
                <Shield className="w-8 h-8 mx-auto text-green-600" />
                <p className="text-xs font-medium">Pagamento 100% Seguro</p>
              </div>
              <div className="space-y-2">
                <CheckCircle className="w-8 h-8 mx-auto text-blue-600" />
                <p className="text-xs font-medium">14 Dias Grátis</p>
              </div>
              <div className="space-y-2">
                <Lock className="w-8 h-8 mx-auto text-purple-600" />
                <p className="text-xs font-medium">Cancele Quando Quiser</p>
              </div>
            </div>

            {/* Plan Summary */}
            <div className="bg-muted/30 rounded-lg p-4 border-2 border-newsletter-primary/20">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-lg">
                    Plano {planName}
                  </p>
                  <p className="text-sm text-muted-foreground">{userEmail}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-newsletter-primary">
                    {pricingData.symbol}{planPrice?.price}
                    <span className="text-sm text-muted-foreground">/{selectedPlan === 'monthly' ? 'mês' : 'ano'}</span>
                  </p>
                  {selectedPlan === 'yearly' && planPrice && 'monthlyEquivalent' in planPrice && (
                    <p className="text-xs text-green-600 font-medium">
                      {pricingData.symbol}{planPrice.monthlyEquivalent}/mês (economia de 50%)
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Stripe Embedded Checkout */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-newsletter-primary/20 border-t-newsletter-primary rounded-full animate-spin" />
              </div>
            )}

            {clientSecret && !loading && (
              <div className="min-h-[400px]">
                <EmbeddedCheckoutProvider 
                  stripe={stripePromise} 
                  options={{ clientSecret }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}

            {/* Fine Print */}
            <p className="text-xs text-center text-muted-foreground">
              🎁 <strong>Trial de 14 dias grátis</strong> • Cobrança automática apenas após o período de teste • 
              Cancele a qualquer momento sem custo
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">{t('inlineCheckout.exitModal.title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-base space-y-3">
              <p>
                {t('inlineCheckout.exitModal.description.part1')} <strong>{t('inlineCheckout.exitModal.description.part2')}</strong> {t('inlineCheckout.exitModal.description.part3')}
              </p>
              <p className="text-newsletter-primary font-semibold text-lg">
                {t('inlineCheckout.exitModal.trial')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('inlineCheckout.exitModal.warning')}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={handleConfirmExit}
              className="w-full sm:w-auto"
            >
              {t('inlineCheckout.exitModal.confirmButton')}
            </Button>
            <Button
              onClick={handleCancelExit}
              className="w-full sm:w-auto bg-newsletter-primary hover:bg-newsletter-primary/90"
            >
              {t('inlineCheckout.exitModal.cancelButton')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
